import HackerPass from "./HackerPass";
import { useEffect, useState } from "react";
import useAuth from "../../hooks/useAuth";
import axiosInstance from "../../api/axios";
import "./dashboard.css";
import DashboardProgress from "./dashboardProgress";
import DashboardButton from "./dashboardButton";
import Confetti from "react-confetti";
import ConfirmationDetails from "./confirmationDetails";
import SchoolEmail from "../../components/SchoolEmail";
import HackerResources from "./HackerResources";
import { canAccessApplications } from "../../utils/princetonAccess";

const FALL_2026_DECISION_DATES = {
  waitlist: "October 16 at 11:59 PM ET",
  acceptance: "October 20 at 11:59 PM ET",
  waitlistRelease: "October 17",
  waitlistAcceptance: "October 20 at 11:59 PM ET",
};

export const DecisionSchedule = ({ state, hackathonStatus, config }) => {
  if (
    config.year !== 2026 || config.cycle !== "fall" ||
    !["released", "confirmation_closed", "waitlist_closed", "second_confirmation_closed"].includes(hackathonStatus) ||
    !["accepted", "waitlisted", "waitlist_confirmed", "waitlist_accepted", "confirmed"].includes(state)
  ) return null;

  return (
    <section className="decision-schedule" aria-labelledby="decision-schedule-heading">
      <h3 id="decision-schedule-heading">Important dates</h3>
      <ul>
        <li>{FALL_2026_DECISION_DATES.waitlist} - Reserve your spot on the waitlist.</li>
        <li>{FALL_2026_DECISION_DATES.acceptance} - Confirm your acceptance.</li>
        <li>{FALL_2026_DECISION_DATES.waitlistRelease} - Waitlist acceptances released by the organizing team; no fixed release time.</li>
        <li>{FALL_2026_DECISION_DATES.waitlistAcceptance} - Confirm your acceptance off the waitlist.</li>
      </ul>
    </section>
  );
};

const hasOpenConfirmationWindow = (state, hackathonStatus) =>
  (state === "accepted" && hackathonStatus === "released") ||
  (state === "waitlist_accepted" &&
    hackathonStatus !== "second_confirmation_closed");

const canViewArrivalDetails = (status, hackathonStatus) =>
  status === "confirmed" ||
  status === "accepted" ||
  status === "waitlist_accepted";

const DashboardTitles = ({ state, hackathonStatus, hasLateApplicationAccess }) => {
  if (hackathonStatus === "not_open") {
    return "Applications Are Not Open Yet";
  } else if (hackathonStatus === "open" || hasLateApplicationAccess) {
    if (state === "unsubmitted") {
      return `Start Your Application Now`;
    } else if (state === "started") {
      return `Continue Your Application Now`;
    } else {
      return "Your Application is Submitted!";
    }
  } else if (state === "started" || state === "unsubmitted")
    return "Applications Are closed";
  else if (hackathonStatus === "closed")
    return "Your Application is Being Reviewed";
  else if (state === "submitted" || state === "rejected")
    return "Thank You for Your Application";
  else if (state === "declined")
    return "We're Sorry You Cannot Attend HackPrinceton!";
  else if (state === "confirmed") return "You're All Set!";
  else if (
    hackathonStatus !== "second_confirmation_closed" &&
    hackathonStatus !== "waitlist_closed" &&
    state === "waitlisted"
  )
    return "You Have Been Waitlisted";
  else if (
    hackathonStatus !== "second_confirmation_closed" &&
    state === "waitlist_confirmed"
  )
    return "You are on the Waitlist!";
  else if (
    hackathonStatus !== "second_confirmation_closed" &&
    state === "waitlist_accepted"
  )
    return "Congratulations, You Have Been Accepted!";
  else if (hackathonStatus === "released" && state === "accepted")
    return "Congratulations, You Have Been Accepted!";
  else if (state === "accepted" || state === "waitlist_accepted")
    return "We're Excited to Welcome You";
  else if (state === "waitlist_confirmed" || state === "waitlisted")
    return "Thank You for Your Application";
  else return "";
};

export const DashboardMessages = ({
  state,
  config,
  hackathonStatus,
  hasLateApplicationAccess,
}) => {
  const isFall2026 = config.year === 2026 && config.cycle === "fall";
  // Default dates for fallback
  let dueDate = "2024-05-20";
  let startDate = "2024-05-21";
  let waitlistDueDate = "2024-05-22";
  let waitlistAcceptDate = "2024-05-22";
  let regularConfirmDate = "2024-05-22";

  // Check if config is properly loaded and contains the required dates
  if (config && config !== "Loading" && config.dates) {
    dueDate = new Date(config.dates.due + "T00:00:00Z");
    startDate = new Date(config.dates.start + "T00:00:00Z");
    waitlistDueDate = new Date(config.dates.waitlist_deadline + "T00:00:00Z");
    waitlistAcceptDate = new Date(
      config.dates.confirmation_second_deadline + "T00:00:00Z"
    );
    regularConfirmDate = new Date(
      config.dates.confirmation_first_deadline + "T00:00:00Z"
    );
  } else {
    dueDate = new Date(dueDate + "T00:00:00Z");
    startDate = new Date(startDate + "T00:00:00Z");
    waitlistDueDate = new Date(waitlistDueDate + "T00:00:00Z");
    waitlistAcceptDate = new Date(waitlistAcceptDate + "T00:00:00Z");
    regularConfirmDate = new Date(regularConfirmDate + "T00:00:00Z");
  }

  const formattedDueDate = dueDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

  const formattedStartDate = startDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

  const formattedWaitlistDueDate = waitlistDueDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

  const formattedRegularDue = regularConfirmDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });

  const formattedWaitlistAccept = waitlistAcceptDate.toLocaleDateString(
    "en-US",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }
  );

  if (hackathonStatus === "not_open") {
    return "Applications for this semester are not yet open. Check back soon to apply!";
  } else if (hackathonStatus === "open" || hasLateApplicationAccess) {
    if (state === "started" || state === "unsubmitted") {
      return hasLateApplicationAccess
        ? "Princeton applicants can still submit after the public deadline. Click launch application below to finish your HackPrinceton application."
        : `Your HackPrinceton Fall 2026 route starts here. Complete and submit your application by ${formattedDueDate}.`;
    } else {
      return "Your application has been submitted and can no longer be edited. We will notify you when a decision is available.";
    }
  } else if (state === "started" || state === "unsubmitted")
    return "Applications are closed for this semester. Check back next year to apply!";
  else if (hackathonStatus === "closed")
    return "Your application has been submitted! We look forward to reviewing your application and will inform you whether you have been accepted soon. ";
  else if (state === "submitted" || state === "rejected")
    return "The organizing team was fortunate enough to receive many talented applicants for this year's cycle. Unfortunately we could not offer you a spot in this year's hackathon but we truly appreciate the effort in your application and hope you will reapply next semester!";
  else if (state === "declined")
    return "We're so sorry you can't make it! Please feel free to apply in future semesters and we hope to see you soon.";
  else if (state === "confirmed")
    return `Thank you for your confirmation, we're so excited to have you at HackPrinceton this semester! We look forward to seeing you at ${config.location} on ${formattedStartDate}.`;
  else if (
    hackathonStatus !== "second_confirmation_closed" &&
    hackathonStatus !== "waitlist_closed" &&
    state === "waitlisted"
  )
    return isFall2026
      ? `While we cannot offer you an initial acceptance, reserve your spot on the waitlist by ${FALL_2026_DECISION_DATES.waitlist} using the form below.`
      : `The organizing team was fortunate enough to receive many talented applicants for this year's cycle. While we cannot offer you an initial acceptance, please fill out the form below by ${formattedWaitlistDueDate} to join the waitlist and still potentially join HackPrinceton!`;
  else if (
    hackathonStatus !== "second_confirmation_closed" &&
    state === "waitlist_confirmed"
  )
    return isFall2026
      ? `Thank you for reserving your spot on the waitlist! Waitlist acceptances will be released on ${FALL_2026_DECISION_DATES.waitlistRelease} by the organizing team. There is no fixed release time; check this portal for your decision.`
      : "Thank you for joining the waitlist! We will let you know as soon as possible whether you have been accepted into HackPrinceton.";
  else if (
    hackathonStatus !== "second_confirmation_closed" &&
    state === "waitlist_accepted"
  )
    return isFall2026
      ? `Congratulations on being accepted to HackPrinceton! Confirm your acceptance off the waitlist by ${FALL_2026_DECISION_DATES.waitlistAcceptance} using the form below.`
      : `Congratulations on being accepted to HackPrinceton! The next step in your HackPrinceton journey is confirming that you will attend. Please confirm below by ${formattedWaitlistAccept}!`;
  else if (hackathonStatus === "released" && state === "accepted")
    return isFall2026
      ? `Congratulations on being accepted to HackPrinceton! Confirm your acceptance by ${FALL_2026_DECISION_DATES.acceptance} using the form below.`
      : `Congratulations on being accepted to HackPrinceton! The next step in your HackPrinceton journey is confirming that you will attend. Please confirm below by ${formattedRegularDue}!`;
  else if (state === "accepted" || state === "waitlist_accepted")
    return "Attendance confirmation is closed. To participate in HackPrinceton, you must confirm your attendance through this portal. If you missed the confirmation deadline, please contact team@hackprinceton.com.";
  else if (state === "waitlist_confirmed" || state === "waitlisted")
    return "The organizing team was fortunate enough to receive many talented applicants for this year's cycle. Unfortunately we could not offer you a spot in this year's hackathon but we truly appreciate the effort in your application and hope you will reapply next semester!";
  else return "";
};

const Dashboard = () => {
  const [schoolEmailVerified, setSchoolEmailVerified] = useState(false);
  const { authState } = useAuth();
  const [email, setEmail] = useState("Loading");
  const [status, setStatus] = useState("Loading");
  const [config, setConfig] = useState("Loading");
  const [numberOfPieces, setNumberOfPieces] = useState(0);
  const [hackathonStatus, setHackathonStatus] = useState("Loading");
  const [teamcode, setTeamCode] = useState(false);
  const [travelReimbursement, setTravelReimbursement] = useState(false);
  const [housingAccomadations, setHousingAccommodations] = useState(false);
  const hasLateApplicationAccess =
    hackathonStatus === "closed" &&
    canAccessApplications(authState.user?.email, hackathonStatus);

  useEffect(() => {
    setEmail(authState.user.email);
  }, [authState.user?.email]);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axiosInstance.get("/api/applications/status");
        // console.log(response);
        setStatus(response.data["status"]);
        setTravelReimbursement(response.data["travel_reimbursement"]);
        setHousingAccommodations(response.data["housing_accommodations"]);
        setTeamCode(response.data["teamcode"]);
      } catch (err) {}
    };

    const fetchConfig = async () => {
      try {
        const response = await axiosInstance.get(
          "/api/applications/cycle/config"
        );
        // console.log(response.data);
        setConfig(response.data);
      } catch (err) {
        // console.log(err);
      }
    };

    const fetchHackathonStatus = async () => {
      try {
        const response = await axiosInstance.get(
          "/api/applications/cycle/status"
        );
        // console.log(response);
        setHackathonStatus(response.data);
      } catch (err) {
        // console.log(err);
      }
    };

    if (authState.isAuthenticated) {
      fetchStatus();
      fetchConfig();
      fetchHackathonStatus();
    }
  }, [authState.isAuthenticated]);

  useEffect(() => {
    if (
      status === "accepted" ||
      status === "confirmed" ||
      status === "waitlist_accepted"
    ) {
      setNumberOfPieces(200);
      const timer = setTimeout(() => {
        setNumberOfPieces(0);
      }, 2000); // Hide confetti after 2 seconds
      return () => clearTimeout(timer); // Cleanup timer on unmount
    }
  }, [status]);

  return (
    <div className="dash-dashboard">
      <div className="row">
        <DashboardProgress status={status} />
      </div>

      <div className="row-dashboard-main">
        <div className="medium-3-columns">
          <h5 className="status-header">Your Info</h5>
          <p className="status-p">{email}</p>
          <h5 className="status-header">Status</h5>
          <p className="status-p">
            {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
          </p>
          {teamcode && (
            <div>
              <h5 className="status-header">Team Code</h5>
              <p className="status-p">{teamcode}</p>
            </div>
          )}
        </div>

        <div className="medium-9-columns">
          <h2 className="status">
            <DashboardTitles
              state={status}
              hackathonStatus={hackathonStatus}
              hasLateApplicationAccess={hasLateApplicationAccess}
            />
          </h2>
          {hackathonStatus === "released" && (
            <Confetti numberOfPieces={numberOfPieces} />
          )}
          {(
            <p className="dashboard-message-p">
              <DashboardMessages
                state={status}
                hackathonStatus={hackathonStatus}
                config={config}
                hasLateApplicationAccess={hasLateApplicationAccess}
              />
            </p>
          ) || ""}

          {status === "confirmed" ? (
            <>
              <HackerPass key={authState.user?.email} />
              <HackerResources />
            </>
          ) : (
            <DecisionSchedule state={status} hackathonStatus={hackathonStatus} config={config} />
          )}

          <SchoolEmail onVerified={setSchoolEmailVerified} />

          {schoolEmailVerified && config.dates && canViewArrivalDetails(status, hackathonStatus) && (
            <ConfirmationDetails
              travelReimbursement={travelReimbursement}
              housingAccomadations={housingAccomadations}
              status={status}
              housing_link={config.housing_link}
              housing_date={config.dates.housing}
              showConfirmationAction={hasOpenConfirmationWindow(
                status,
                hackathonStatus
              )}
            />
          )}

          <DashboardButton status={status} email={email} schoolEmailVerified={schoolEmailVerified} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
